import React, { useCallback } from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { mediaQueryUp } from 'components/utils/breakpoint'

const TOGGLE_CONTAINER_PADDING = 8
const TOGGLE_BUTTON_GAP = 4
const INDICATOR_WIDTH_ADJUSTMENT = TOGGLE_CONTAINER_PADDING + (TOGGLE_BUTTON_GAP / 2)

const ToggleButtonsContainer = styled(Group)`
  position: relative;
  padding: ${rem(TOGGLE_CONTAINER_PADDING)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const SlidingIndicator = styled.div<{ $isOptionBSelected: boolean }>`
  position: absolute;
  top: ${rem(TOGGLE_CONTAINER_PADDING)};
  left: ${rem(TOGGLE_CONTAINER_PADDING)};
  height: ${rem(44)};
  background-color: ${props => props.theme.palette.brand.primary.normal};
  box-shadow: ${props => props.theme.shadow.flat.small};
  border-radius: ${props => props.theme.borderRadius.round};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  z-index: 0;

  /* Calculate width based on flex: 1 (50% each minus gap) */
  width: calc(50% - ${rem(INDICATOR_WIDTH_ADJUSTMENT)});

  ${mediaQueryUp.tablet} {
    width: ${rem(160)};
  }

  transform: ${props => props.$isOptionBSelected
    ? `translateX(calc(100% + ${rem(TOGGLE_BUTTON_GAP)}))`
    : 'translateX(0)'
  };
`

const ToggleButton = styled(Clickable)`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${rem(44)};
  padding: ${rem(4)} ${rem(16)};
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: transparent;
  transition: color 0.2s;
  outline-offset: 1px;
  position: relative;
  z-index: 1;

  ${mediaQueryUp.tablet} {
    flex: initial;
    min-width: ${rem(160)};
  }

  &:focus {
    ${interactiveFocusStyle}
  }

  &:not(.is-selected):not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.default.six};
      }
    }
  }
`

export interface ToggleOption {
  label: string
  value: string
  displayPrice?: number
  buttonStartIcon?: React.ReactNode
}

interface Props {
  optionA: ToggleOption
  optionB: ToggleOption
  selectedValue: string
  unavailableMessage?: string
  isMemberPrice?: boolean
  pricesLoading?: boolean
  disableOptionB?: boolean
  onOptionChange: (value: string) => void
}

function ToggleButtonContainer(props: Props) {
  const {
    optionA,
    optionB,
    selectedValue,
    unavailableMessage,
    isMemberPrice,
    pricesLoading,
    disableOptionB,
    onOptionChange,
  } = props

  const isOptionASelected = selectedValue === optionA.value
  const isOptionBSelected = selectedValue === optionB.value

  const onSelectOptionA = useCallback(() => {
    if (!isOptionASelected) {
      onOptionChange(optionA.value)
    }
  }, [isOptionASelected, onOptionChange, optionA.value])

  const onSelectOptionB = useCallback(() => {
    if (!isOptionBSelected && !disableOptionB) {
      onOptionChange(optionB.value)
    }
  }, [isOptionBSelected, disableOptionB, onOptionChange, optionB.value])

  return (
    <ToggleButtonsContainer direction="horizontal" gap={4}>
      {/* Sliding Background Indicator */}
      <SlidingIndicator $isOptionBSelected={isOptionBSelected} />

      {/* Option A */}
      <ToggleButton className={cn({ 'is-selected': isOptionASelected })} onClick={onSelectOptionA}>
        <Group direction="vertical" horizontalAlign="center">
          <BodyTextBlock
            variant="medium"
            weight={isOptionASelected ? 'bold' : undefined}
            colour={isOptionASelected ? 'neutral-eight' : 'neutral-one'}
            startIcon={optionA.buttonStartIcon}
            wrap="no-wrap"
          >
            {optionA.label}
          </BodyTextBlock>
          {!pricesLoading && optionA.displayPrice !== undefined && optionA.displayPrice > 0 && (
            <BodyText variant="small" colour={isOptionASelected ? 'neutral-five' : 'neutral-three'}>
              {isMemberPrice && 'From '}
              <FormatCurrency format="roundedDollar" value={optionA.displayPrice} />
            </BodyText>
          )}
        </Group>
      </ToggleButton>

      {/* Option B */}
      <ToggleButton
        className={cn({ 'is-selected': isOptionBSelected })}
        disabled={disableOptionB}
        onClick={onSelectOptionB}
      >
        <Group direction="vertical" horizontalAlign="center">

          <BodyTextBlock
            variant="medium"
            weight={isOptionBSelected ? 'bold' : undefined}
            colour={isOptionBSelected ? 'neutral-eight' : 'neutral-one'}
            startIcon={optionB.buttonStartIcon}
            wrap="no-wrap"
          >
            {optionB.label}
          </BodyTextBlock>

          {!pricesLoading && !disableOptionB && optionB.displayPrice !== undefined && optionB.displayPrice > 0 && (
            <BodyText variant="small" colour={isOptionBSelected ? 'neutral-five' : 'neutral-three'}>
              {isMemberPrice && 'From '}
              <FormatCurrency format="roundedDollar" value={optionB.displayPrice} />
            </BodyText>
          )}
          {!pricesLoading && disableOptionB && unavailableMessage && (
            <BodyText variant="small" colour={isOptionBSelected ? 'neutral-five' : 'neutral-three'}>
              {unavailableMessage}
            </BodyText>
          )}
        </Group>
      </ToggleButton>
    </ToggleButtonsContainer>
  )
}

export default ToggleButtonContainer
